import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import planDeCuentasData from '../../components/planDeCuentas/planDeCuentasData.json';
import FeatherIcon from 'feather-icons-react';
import { useAccounts } from '../../context/AccountsContext';
import { extractLevel4Items } from './planDeCuentaDropdownOptions';
import './PlanDeCuentaDropdown.scss';

/** Sidebar nav usa z-index 1030; modales CustomModal ~100000. */
export const PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX = {
  PAGE: 1020,
  MODAL: 100010,
};

const VIEWPORT_EDGE_MARGIN = 8;

/** Ajusta posición y altura del menú portal para que quepa en viewport y el scroll interno funcione. */
function computePortalMenuLayout(triggerRect, preferredMaxHeight = 320) {
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_EDGE_MARGIN;
  const spaceAbove = triggerRect.top - VIEWPORT_EDGE_MARGIN;
  const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
  const available = (openDown ? spaceBelow : spaceAbove) - 4;
  const maxHeight = Math.max(120, Math.min(preferredMaxHeight, available));
  const top = openDown
    ? triggerRect.bottom + 4
    : Math.max(VIEWPORT_EDGE_MARGIN, triggerRect.top - maxHeight - 4);

  return {
    top,
    left: triggerRect.left,
    width: triggerRect.width,
    maxHeight,
  };
}

const PlanDeCuentaDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Seleccione una cuenta",
  className = "",
  disabled = false,
  clearable = true,
  id,
  loadingText,
  menuMaxHeight = 320, // Default max height in pixels
  /** Menú en portal (body) — usar dentro de modales o contenedores con overflow para no recortar el menú (mismo criterio que EOAFCategoryDropdown). */
  portalMenu = false,
  /** Opciones ya calculadas (p. ej. listas largas con muchos dropdowns en la misma vista). */
  precomputedOptions,
  /** Oculta el código de cada opción. Lo usan las cuentas maestras, cuya clave
   *  se confunde con un código contable y no identifica nada para el usuario. */
  hideOptionKey = false,
  menuZIndex,
  ...props 
}) => {
  const effectiveMenuZIndex =
    menuZIndex ??
    (portalMenu ? PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL : PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.PAGE);
  const { accounts, loading } = useAccounts();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [portalLayout, setPortalLayout] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  // Use context data or fallback to static JSON
  const planDeCuentasSource = accounts?.length > 0 ? accounts : planDeCuentasData;

  const allOptions = useMemo(() => {
    if (precomputedOptions) return precomputedOptions;
    return extractLevel4Items(planDeCuentasSource);
  }, [precomputedOptions, planDeCuentasSource]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return allOptions;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = [];
    let lastSectionKey = null;
    
    allOptions.forEach(option => {
      if (option.isSection) {
        // Check if section title matches
        if (option.text.toLowerCase().includes(query)) {
          filtered.push(option);
          lastSectionKey = option.sectionKey;
        }
      } else {
        // Check if option matches
        const matches = option.text.toLowerCase().includes(query) ||
          option.key.toLowerCase().includes(query) ||
          option.label.toLowerCase().includes(query);
        
        if (matches) {
          // Add section header if it's different from last one
          if (option.sectionKey !== lastSectionKey) {
            // Find the section header for this option
            const sectionHeader = allOptions.find(opt => 
              opt.isSection && opt.sectionKey === option.sectionKey
            );
            if (sectionHeader) {
              filtered.push(sectionHeader);
              lastSectionKey = option.sectionKey;
            }
          }
          filtered.push(option);
        }
      }
    });
    
    return filtered;
  }, [allOptions, searchQuery]);

  // Update selectedText when value changes
  useEffect(() => {
    if (value) {
      const selectedOption = allOptions.find(opt => opt.value === value);
      if (selectedOption) {
        setSelectedText(selectedOption.text);
      }
    } else {
      setSelectedText('');
    }
  }, [value, allOptions]);

  const updatePortalLayout = () => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    setPortalLayout(computePortalMenuLayout(rect, menuMaxHeight));
  };

  useLayoutEffect(() => {
    if (!portalMenu || !isOpen || !dropdownRef.current) {
      setPortalLayout(null);
      return;
    }
    updatePortalLayout();
  }, [portalMenu, isOpen, menuMaxHeight, filteredOptions.length]);

  useEffect(() => {
    if (!portalMenu || !isOpen) return;
    const handleScrollOrResize = () => updatePortalLayout();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [portalMenu, isOpen, menuMaxHeight]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = portalMenu ? menuRef.current?.contains(event.target) : false;
      if (portalMenu) {
        if (!inTrigger && !inMenu) {
          setIsOpen(false);
          setSearchQuery('');
        }
      } else if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, portalMenu]);

  // Handle option selection
  const handleSelect = (option) => {
    setSelectedText(option.text);
    setIsOpen(false);
    setSearchQuery('');
    
    if (onChange) {
      onChange({}, {
        value: option.value,
        text: option.text,
        label: option.label,
        account: option.account
      });
    }
  };

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      // Find first non-section option
      const firstSelectableOption = filteredOptions.find(opt => !opt.isSection);
      if (firstSelectableOption) {
        handleSelect(firstSelectableOption);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  // Handle clearing selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedText('');
    setIsOpen(false);
    setSearchQuery('');
    
    if (onChange) {
      onChange({}, {
        value: '',
        text: '',
        label: '',
        account: null
      });
    }
  };

  // Pass loading state to display loading indicator
  const displayValue = (loading && !selectedText) ? 'Cargando cuentas...' : (loadingText && !selectedText ? loadingText : (isOpen ? searchQuery : selectedText));
  const showPlaceholder = !selectedText && !isOpen && !loadingText && !loading;

  const optionsMaxHeight = portalMenu && portalLayout
    ? portalLayout.maxHeight
    : menuMaxHeight;

  const menuList = (
    <>
      {filteredOptions.length > 0 ? (
        <ul 
          className="plan-de-cuenta-dropdown-options" 
          role="listbox"
          style={
            portalMenu
              ? { maxHeight: optionsMaxHeight, minHeight: 0 }
              : { maxHeight: `${menuMaxHeight}px` }
          }
        >
          {filteredOptions.map((option) => (
            <li
              key={option.key}
              className={`plan-de-cuenta-dropdown-option ${option.isSection ? 'section-header' : ''} ${value === option.value ? 'selected' : ''}`}
              onClick={() => !option.isSection && handleSelect(option)}
              role={option.isSection ? 'presentation' : 'option'}
              aria-selected={option.isSection ? undefined : value === option.value}
            >
              {option.isSection ? (
                <span className="plan-de-cuenta-option-text">{option.text}</span>
              ) : (
                <span className="plan-de-cuenta-option-text">
                  {/*
                    El código se muestra por defecto: en el plan CONTABLE es
                    parte de cómo se identifica una cuenta. En las cuentas
                    maestras no —se confunde con un código contable— y por eso
                    `hideOptionKey` lo apaga sin tocar la otra pantalla.
                  */}
                  {!hideOptionKey && (
                    <span className="plan-de-cuenta-option-key">{option.key}</span>
                  )}
                  <span className="plan-de-cuenta-option-name">{option.label}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="plan-de-cuenta-dropdown-no-results">
          <div className="plan-de-cuenta-no-results-message">
            {searchQuery.trim() 
              ? 'No se encontraron cuentas que coincidan con la búsqueda'
              : 'Ingresa el código o nombre de la cuenta para buscar'}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div 
      ref={dropdownRef}
      className={`plan-de-cuenta-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${clearable ? '' : 'no-clear'}`}
      {...props}
    >
      <div className="plan-de-cuenta-dropdown-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="plan-de-cuenta-dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        />
        {clearable && selectedText && !isOpen && !disabled && (
          <button
            type="button"
            className="plan-de-cuenta-dropdown-clear-button"
            onClick={handleClear}
            title="Limpiar"
            tabIndex={0}
          >
            <FeatherIcon icon="x" size={16} />
          </button>
        )}
        <span className="plan-de-cuenta-dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
      </div>
      {isOpen && !disabled && (
        portalMenu && portalLayout
          ? createPortal(
              <div
                ref={menuRef}
                className="plan-de-cuenta-dropdown-menu plan-de-cuenta-dropdown-menu-portal"
                style={{
                  position: 'fixed',
                  top: portalLayout.top,
                  left: portalLayout.left,
                  minWidth: Math.max(portalLayout.width, 220),
                  maxHeight: portalLayout.maxHeight,
                  zIndex: effectiveMenuZIndex,
                }}
                onWheel={(e) => e.stopPropagation()}
              >
                {menuList}
              </div>,
              document.body
            )
          : !portalMenu ? (
            <div 
              className="plan-de-cuenta-dropdown-menu"
              style={{ maxHeight: `${menuMaxHeight}px` }}
            >
              {menuList}
            </div>
          ) : null
      )}
    </div>
  );
};

export default PlanDeCuentaDropdown;

